import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { examsSelectors } from '../slices/exams/examsSlice';
import type { RootState } from '../store/store';
import { useActivateExam } from './useActivateExam';

export const useActivateExamFromUrl = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const exams = useSelector(examsSelectors.selectAll);
  const examsStatus = useSelector((state: RootState) => state.exams.status);
  const { activateExam } = useActivateExam();
  const { enqueueSnackbar } = useSnackbar();
  const handledExamIdRef = useRef<string | null>(null);

  useEffect(() => {
    const examId = searchParams.get('activateExam');

    // Once the param is gone (we strip it after handling), forget the id so a
    // later click on the same notification activates the exam again. While
    // the param is still present the ref stops re-handling across refetches.
    if (!examId) {
      handledExamIdRef.current = null;
      return;
    }

    if (examsStatus !== 'succeeded' || handledExamIdRef.current === examId) {
      return;
    }

    handledExamIdRef.current = examId;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('activateExam');

    const exam = exams.find(({ id }) => id === examId);
    if (!exam) {
      enqueueSnackbar('That exam is no longer available', { variant: 'error' });
      setSearchParams(nextSearchParams, { replace: true });
      return;
    }

    void (async () => {
      await activateExam(exam);
      setSearchParams(nextSearchParams, { replace: true });
    })();
  }, [
    activateExam,
    enqueueSnackbar,
    exams,
    examsStatus,
    searchParams,
    setSearchParams,
  ]);
};
